import React, { useState } from 'react';
import { Clock, Calendar, Anchor, Building, ChevronDown, ChevronUp, Truck } from 'lucide-react';
import { formatDate } from '../../../components/Common/CommonLogic';

const SailingScheduleItems = ({ route }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpanded = () => setIsExpanded(!isExpanded);

    const calculateDaysRemaining = () => {
        if (route?.destination?.estimated_date) {
            const destinationDate = new Date(route.destination.estimated_date);
            const currentDate = new Date();
            const diffTime = destinationDate - currentDate;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays > 0 ? diffDays : 0;
        }
        return null;
    };

    const daysRemaining = calculateDaysRemaining();
    const carrierName = route?.carrier_name || '-';
    const carrierScac = route?.carrier_scac || '-';
    const originPort = route?.origin?.port_name || '-';
    const destinationPort = route?.destination?.port_name || '-';
    const startDate = route?.origin?.estimated_date ? formatDate(route.origin.estimated_date) : '-';
    const endDate = route?.destination?.estimated_date ? formatDate(route.destination.estimated_date) : '-';

    return (
        <div className="py-4">
            <div className="px-2" style={{ width: '100%' }}>
                {/* Main Card */}
                <div className="bg-white rounded shadow-sm border overflow-hidden">
                    {/* Clickable Header */}
                    <div className="p-4 pointer hover-shadow" style={{ cursor: 'pointer' }} onClick={toggleExpanded}>
                        {/* Header with OOCL branding and duration/dates */}
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <div className="d-flex align-items-center gap-2">
                                <div className="d-flex align-items-center justify-content-center bg-danger rounded" style={{ width: 32, height: 32 }}>
                                    <span className="text-white small fw-bold">{carrierScac}</span>
                                </div>
                                <span className="fs-4 fw-bold text-dark ms-2">{carrierName}</span>
                            </div>
                            <div className="d-flex align-items-center gap-3 text-secondary small">
                                <div className="d-flex align-items-center gap-1">
                                    <Clock size={14} />
                                    <span>{daysRemaining} days</span>
                                </div>
                                <div className="d-flex align-items-center gap-1">
                                    <Calendar size={14} />
                                    <span>{startDate} — {endDate}</span>
                                </div>
                                <div className="ms-3">
                                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                </div>
                            </div>
                        </div>

                        {/* Route Section */}
                        <div>
                            {/* Cities */}
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div className="d-flex align-items-center gap-2">
                                    <Anchor size={16} className="text-secondary" />
                                    <span className="fs-5 fw-semibold text-dark">{originPort}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <span className="fs-5 fw-semibold text-dark">{destinationPort}</span>
                                    <Building size={16} className="text-secondary" />
                                </div>
                            </div>

                            {/* Progress Bar */}
                            <div className="position-relative mb-2">
                                <div className="d-flex align-items-center">
                                    {/* Start circle */}
                                    <div className="rounded-circle bg-primary border border-white shadow-sm" style={{ width: 12, height: 12, zIndex: 2 }}></div>
                                    {/* First segment - blue */}
                                    {route?.legs?.map((leg, index) => (
                                        <div key={index} className="flex-grow-1 mx-1 position-relative" style={{ height: 4, background: '#0d6efd' }}>
                                            <div className="position-absolute end-0 top-50 translate-middle-y">
                                                <div className="rounded-circle bg-primary border border-white shadow-sm" style={{ width: 12, height: 12 }}></div>
                                            </div>
                                        </div>
                                    ))}
                                    {/* {route?.legs?.length > 1 && (
                                        <div className="flex-grow-1 mx-1 position-relative" style={{ height: 4, background: '#0d6efd' }}>
                                            <div className="position-absolute end-0 top-50 translate-middle-y">
                                                <div className="rounded-circle bg-primary border border-white shadow-sm" style={{ width: 12, height: 12 }}></div>
                                            </div>
                                        </div>
                                    )} */}

                                    {/* Second segment - teal/green */}
                                    {/* <div className="flex-grow-1 mx-1 position-relative" style={{ height: 4, background: '#20c997' }}>
                                        <div className="position-absolute end-0 top-50 translate-middle-y">
                                            <div className="rounded-circle" style={{ background: '#20c997', width: 12, height: 12, border: '2px solid #fff', boxShadow: '0 1px 2px rgba(0,0,0,.05)' }}></div>
                                        </div>
                                    </div> */}
                                    {/* End circle */}
                                    <div className="rounded-circle bg-secondary border border-white shadow-sm" style={{ width: 12, height: 12 }}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Expandable Content */}
                    {isExpanded && (
                        <div className="border-top">
                            <div className="px-4 py-4">
                                {/* Route Details Header */}
                                <div className="mb-4">
                                    <div className="row text-secondary fw-medium mb-3">
                                        <div className="col-6">Route</div>
                                        <div className="col-3">Transport</div>
                                        <div className="col-3">Service</div>
                                    </div>
                                    {/* Route Details */}
                                    <div className="d-grid gap-3">
                                        {/* First Leg: Chennai to Singapore */}
                                        {route?.legs?.map((leg, index) => (
                                            <div key={index} className="row align-items-start py-3 bg-white rounded border mb-2">
                                                <div className="col-6 d-flex align-items-start gap-2">
                                                    <div className="row align-items-center">
                                                        <div className="col-auto d-flex align-items-center">
                                                            <Anchor size={16} className="me-1 flex-shrink-0" />
                                                            <div>
                                                                <div className="fw-medium text-dark">{leg?.departure?.port_name}, {leg?.departure?.port_locode}</div>
                                                                <div className="text-secondary small">{formatDate(leg?.departure?.estimated_date)}</div>
                                                            </div>
                                                        </div>
                                                        <div className="col-auto mx-3">
                                                            <span className="fw-bold fs-5">→</span>
                                                        </div>
                                                        <div className="col-auto d-flex align-items-center">
                                                            <Anchor size={16} className="me-1 flex-shrink-0" />
                                                            <div>
                                                                <div className="fw-medium text-dark">{leg?.arrival?.port_name}, {leg?.arrival?.port_locode}</div>
                                                                <div className="text-secondary small">{formatDate(leg?.arrival?.estimated_date)}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-3">
                                                    <div className="fw-medium text-dark">{leg?.vessel_name}</div>
                                                    <div className="small text-decoration-underline text-secondary">{leg?.voyages?.[0]?.voyage}</div>
                                                </div>
                                                <div className="col-3">
                                                    <div className="fw-medium text-dark">{leg?.service_name}</div>
                                                    <div className="small text-secondary">{leg?.service_name}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {/* Deadlines Section */}
                                <div className="mb-4 p-3 bg-opacity-10 rounded border border-primary">
                                    <div className="text-primary fw-medium mb-2">Deadlines</div>
                                    <div className="row">
                                        {route?.cut_off_dates?.map((deadline, index) => (
                                            <div key={index} className="col text-start">
                                                <div className="fw-semibold text-dark">{deadline?.name}</div>
                                                <div className="small text-secondary">{formatDate(deadline?.date)}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {/* Action Buttons */}
                                {/* <div className="d-flex justify-content-end gap-2">
                                    <button className="btn btn-outline-primary">
                                        🔗 Copy link
                                    </button>
                                    <button className="btn btn-outline-primary">
                                        View Details
                                    </button>
                                    <button className="btn btn-primary">
                                        Show rates
                                    </button>
                                </div> */}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SailingScheduleItems;